import { AvatarSize } from "@components/Avatar";
import { Button, ButtonTargetKind } from "@components/Button";
import { Card } from "@components/Card";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { useState } from "react";

import { displayNonprofitLocation } from "@every.org/common/src/helpers/nonprofitLocation";

import { NonprofitAvatar } from "src/components/Avatar/NonprofitAvatar";
import { ProfilePageHeaderCard } from "src/components/ProfilePageHeaderCard";
import { PageSection } from "src/components/layout/PageSection";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { CreateMultiNonprofitFundraiserModal } from "src/pages/Fundraiser/CreateModal";
import { HeaderCardMetadata } from "src/pages/Fundraiser/CreatePage/styles";
import { FundraiserNonprofitsPopover } from "src/pages/Fundraiser/FundraiserNonprofitsPopover";
import { ButtonRole, ButtonSize } from "src/styles/button";
import { colorCssVars } from "src/theme/color";
import { horizontalStackCss, verticalStackCss } from "src/theme/spacing";

interface HeaderCardPageSectionProps {
  nonprofit: ContextNonprofit;
}

export function HeaderCardPageSection({
  nonprofit,
}: HeaderCardPageSectionProps) {
  const { name, locationAddress, description } = nonprofit;
  return (
    <PageSection>
      <ProfilePageHeaderCard
        css={{ alignItems: "center" }}
        title={name}
        subtitle={
          locationAddress
            ? displayNonprofitLocation({ locationAddress })
            : undefined
        }
        avatar={(avatarCss) => (
          <NonprofitAvatar
            nonprofit={nonprofit}
            size={AvatarSize.MEDIUM}
            disableLink
            customCSS={avatarCss}
          />
        )}
        metadata={
          <HeaderCardMetadata>
            <span>{description}</span>
          </HeaderCardMetadata>
        }
      />
    </PageSection>
  );
}

export function MultiNonprofitFundraiserHeaderCardPageSection({
  forNonprofits,
  onEdit,
}: {
  forNonprofits: string[];
  onEdit: (ids: string[]) => void;
}) {
  const [showModal, setShowModal] = useState(false);

  return (
    <PageSection>
      <Card css={[horizontalStackCss.m, { alignItems: "center" }]}>
        <Icon
          iconImport={() =>
            import("@components/Icon/icons/ThermometerStarsIcon")
          }
          size={IconSize.XX_LARGE}
          display={IconDisplay.SECONDARY}
          css={{ width: "72px", height: "72px" }}
        />
        <div css={verticalStackCss.xxs}>
          <h2>Fundraiser</h2>
          <span css={{ color: `var(${colorCssVars.text.secondary})` }}>
            Supporting{" "}
            <FundraiserNonprofitsPopover nonprofitIds={forNonprofits} />
          </span>
        </div>
        <Button
          data-tname="EditNonprofits"
          size={ButtonSize.SMALL}
          role={ButtonRole.SECONDARY}
          css={{
            marginLeft: "auto",
          }}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: () => setShowModal(true),
          }}
          contentCss={[horizontalStackCss.s, { alignItems: "center" }]}
        >
          <Icon
            iconImport={() => import("@components/Icon/icons/OrgMulti")}
            size={IconSize.MEDIUM}
            display={IconDisplay.ACCENT}
          />
          <span>Edit nonprofits</span>
        </Button>
        <CreateMultiNonprofitFundraiserModal
          isModalOpen={showModal}
          onRequestClose={() => setShowModal(false)}
          onEdit={onEdit}
          forNonprofits={forNonprofits}
        />
      </Card>
    </PageSection>
  );
}
